import {
  createFileRoute,
  Outlet,
  redirect,
  useChildMatches,
  useNavigate,
} from '@tanstack/react-router'
import type { SalesOrderFilters, SalesOrderStatus } from '@/features/sales-orders/api'
import { OrderList } from '@/features/sales-orders/order-list'
import { hasGrant } from '@/lib/me'

/**
 * Route shell for the sales order list.
 *
 * `orders.$orderId.index.tsx`, `orders.new.tsx` and `orders.$orderId.edit.tsx`
 * are CHILDREN of this route in flat file routing (mirrors
 * `_authenticated/purchasing/orders.tsx`): without the Outlet below, their
 * URLs render this list instead of themselves (apps/company/AGENTS.md).
 */
export const Route = createFileRoute('/_authenticated/sales/orders')({
  validateSearch: (search: Record<string, unknown>): SalesOrderFilters => ({
    view: search.view === 'quotations' || search.view === 'orders' ? search.view : undefined,
    status: isStatus(search.status) ? search.status : undefined,
    customer: typeof search.customer === 'string' && search.customer ? search.customer : undefined,
    warehouse:
      typeof search.warehouse === 'string' && search.warehouse ? search.warehouse : undefined,
    from: typeof search.from === 'string' ? search.from : undefined,
    to: typeof search.to === 'string' ? search.to : undefined,
    search: typeof search.search === 'string' && search.search ? search.search : undefined,
    hasInvoice:
      search.hasInvoice === 'yes' || search.hasInvoice === 'no' ? search.hasInvoice : undefined,
    sort: isSort(search.sort) ? search.sort : undefined,
  }),
  beforeLoad: ({ context }) => {
    if (!hasGrant(context.queryClient, 'sales_order.view')) {
      throw redirect({ to: '/' })
    }
  },
  component: SalesOrdersRoute,
})

function SalesOrdersRoute() {
  const navigate = useNavigate()
  const children = useChildMatches()
  const params = Route.useSearch()

  if (children.length > 0) {
    return <Outlet />
  }

  return (
    <OrderList
      params={params}
      onParamsChange={(next) => void navigate({ to: '/sales/orders', search: next })}
    />
  )
}

const STATUSES: SalesOrderStatus[] = ['draft', 'reserved', 'confirmed', 'cancelled']

function isStatus(value: unknown): value is SalesOrderStatus {
  return typeof value === 'string' && (STATUSES as string[]).includes(value)
}

const SORTS: NonNullable<SalesOrderFilters['sort']>[] = [
  'order_date',
  '-order_date',
  'required_date',
  '-required_date',
  'total_amount',
  '-total_amount',
]

function isSort(value: unknown): value is NonNullable<SalesOrderFilters['sort']> {
  return typeof value === 'string' && (SORTS as string[]).includes(value)
}
